import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  /** id of the heading that labels the dialog, for aria-labelledby */
  labelledBy?: string
  /** extra classes for the panel, e.g. width (`sm:max-w-sm`) */
  panelClassName?: string
  /** clicking the backdrop closes the modal (default true) */
  closeOnBackdrop?: boolean
}

/**
 * Shared Tailwind modal shell: backdrop + centered panel with an open/close
 * animation, escape-to-close and body scroll lock. Consumers pass the panel
 * body as children.
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  labelledBy,
  panelClassName = 'sm:max-w-lg',
  closeOnBackdrop = true,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  // Keep the content shown while open so it stays put during the close animation.
  // Without this a consumer that clears props on close (e.g. an empty title) makes
  // the panel shrink mid-animation and the modal looks like it jumps.
  const lastContent = useRef<React.ReactNode>(null)
  if (isOpen) lastContent.current = children

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsAnimating(true))
      })
    } else {
      setIsAnimating(false)
      const timer = setTimeout(() => setIsVisible(false), 200)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  if (!isVisible) return null

  return (
    <div className="relative z-50" aria-labelledby={labelledBy} role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-gray-500/75 transition-opacity duration-300 ease-out ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Panel container */}
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
          <div
            className={`relative w-full transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all duration-300 ease-out sm:my-8 ${panelClassName} ${
              isAnimating
                ? 'opacity-100 translate-y-0 sm:scale-100'
                : 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {isOpen ? children : lastContent.current}
          </div>
        </div>
      </div>
    </div>
  )
}
